import { useState, useEffect } from 'react';
import { InjectionSites } from '../lib/data';

const C = {
  bg: '#070B10', surface: '#0D1520', border: '#1A2A40',
  accent: '#00D4FF', text: '#E8F0F8', muted: '#5A7A99',
  green: '#00E5A0', yellow: '#FFD166', red: '#FF4D6A',
  mono: "'JetBrains Mono','Fira Mono',monospace",
  sans: "'Inter',system-ui,sans-serif",
};

const SITES = [
  // Abdomen - tighter around center
  { id: 'abd-1', name: 'Abdomen UL', zone: 'abdomen', x: 45, y: 42, tip: '2 inches from navel' },
  { id: 'abd-2', name: 'Abdomen UC', zone: 'abdomen', x: 50, y: 42, tip: 'Above navel line' },
  { id: 'abd-3', name: 'Abdomen UR', zone: 'abdomen', x: 55, y: 42, tip: '2 inches from navel' },
  { id: 'abd-4', name: 'Abdomen ML', zone: 'abdomen', x: 45, y: 47, tip: 'Pinch & inject' },
  { id: 'abd-5', name: 'Abdomen MC', zone: 'abdomen', x: 50, y: 47, tip: 'Avoid navel area' },
  { id: 'abd-6', name: 'Abdomen MR', zone: 'abdomen', x: 55, y: 47, tip: 'Pinch & inject' },
  { id: 'abd-7', name: 'Abdomen LL', zone: 'abdomen', x: 45, y: 52, tip: 'Below navel' },
  { id: 'abd-8', name: 'Abdomen LC', zone: 'abdomen', x: 50, y: 52, tip: 'Lower abdomen' },
  { id: 'abd-9', name: 'Abdomen LR', zone: 'abdomen', x: 55, y: 52, tip: 'Below navel' },
  { id: 'Quad-lu', name: 'Quad L Upper', zone: 'thigh', x: 36, y: 60, tip: 'Outer thigh' },
  { id: 'Quad-ll', name: 'Quad L Lower', zone: 'thigh', x: 37, y: 64, tip: 'Outer thigh' },
  { id: 'Quad-ru', name: 'Quad R Upper', zone: 'thigh', x: 68, y: 60, tip: 'Outer thigh' },
  { id: 'Quad-rl', name: 'Quad R Lower', zone: 'thigh', x: 68, y: 64, tip: 'Outer thigh' },
  { id: 'Delt-l', name: 'Delt L', zone: 'arm', x: 23, y: 29, tip: 'Back of arm' },
  { id: 'Delt-r', name: 'Delt R', zone: 'arm', x: 82, y: 29, tip: 'Back of arm' },
  { id: 'glute-lu', name: 'Ventro Glute', zone: 'glute', x: 30, y: 50, tip: 'Upper outer quad' },
  { id: 'glute-ru', name: 'Ventro Glute', zone: 'glute', x: 67, y: 50, tip: 'Upper outer quad' },

];

const IMAGES = {
  front: '/images/body-front.jpg',
  back: '/images/body-back.jpg'
};

function BodyDiagram({ side, sites, hoveredSite, setHoveredSite, onSiteClick }) {
  const frontSites = SITES.filter(s => s.zone !== 'glute');
  const backSites = SITES.filter(s => s.zone === 'glute');
  const displaySites = side === 'front' ? frontSites : backSites;

  const getSiteData = (siteId) => {
    return sites.find(s => s.site_name === siteId) || { site_name: siteId, last_used: null };
  };

  const getColor = (lastUsed) => {
    if (!lastUsed) return C.green;
    const today = new Date();
    const days = Math.floor((today - new Date(lastUsed)) / (1000 * 60 * 60 * 24));
    if (days >= 14) return C.green;
    if (days >= 7) return C.yellow;
    return C.red;
  };

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 20, display: 'flex', justifyContent: 'center' }}>
      <div style={{ position: 'relative', width: '100%', maxWidth: 320 }}>
        <img 
          src={side === 'front' ? IMAGES.front : IMAGES.back}
          alt={`${side} view body`}
          style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 4 }} 
        />
        <div style={{ position: 'absolute', inset: 0 }}>
          {displaySites.map(site => {
            const siteData = getSiteData(site.id);
            const color = getColor(siteData.last_used);
            const isHovered = hoveredSite === site.id;

            return (
              <button
                key={site.id}
                onClick={() => onSiteClick(site.id)}
                onMouseEnter={() => setHoveredSite(site.id)}
                onMouseLeave={() => setHoveredSite(null)}
                style={{
                  position: 'absolute',
                  left: `${site.x}%`,
                  top: `${site.y}%`,
                  transform: 'translate(-50%, -50%)',
                  width: isHovered ? 32 : 24,
                  height: isHovered ? 32 : 24,
                  borderRadius: '50%',
                  border: `${isHovered ? 3 : 2}px solid ${color}`,
                  background: 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: isHovered ? `0 0 12px ${color}40` : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div style={{ width: isHovered ? 8 : 6, height: isHovered ? 8 : 6, borderRadius: '50%', background: color, transition: 'all 0.2s' }} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function InjectionSitesTab() {
  const [sites, setSites] = useState([]);
  const [side, setSide] = useState('front');
  const [hoveredSite, setHoveredSite] = useState(null);
  const today = new Date();

  useEffect(() => {
    InjectionSites.list().then(setSites).catch(err => console.error('Failed to load sites:', err));
  }, []);

  const getSiteData = (siteId) => {
    return sites.find(s => s.site_name === siteId) || { site_name: siteId, last_used: null };
  };

  const getColor = (lastUsed) => {
    if (!lastUsed) return C.green;
    const days = Math.floor((today - new Date(lastUsed)) / (1000 * 60 * 60 * 24));
    if (days >= 14) return C.green;
    if (days >= 7) return C.yellow;
    return C.red;
  };

  const getDaysAgo = (lastUsed) => {
    if (!lastUsed) return 'Never';
    const days = Math.floor((today - new Date(lastUsed)) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    return `${days}d`;
  };

  const logInjection = async (siteId) => {
    try {
      const existing = sites.find(s => s.site_name === siteId);
      const dateStr = today.toISOString().split('T')[0];
      if (existing) {
        await InjectionSites.update(existing.id, { last_used: dateStr });
      } else {
        await InjectionSites.add({ site_name: siteId, last_used: dateStr });
      }
      const updated = await InjectionSites.list();
      setSites(updated);
    } catch (err) {
      console.error('Failed to log injection:', err);
    }
  };

  const frontSites = SITES.filter(s => s.zone !== 'glute');
  const backSites = SITES.filter(s => ['glute', 'thigh', 'arm'].includes(s.zone));
  const displaySites = side === 'front' ? frontSites : backSites;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <div style={{ fontFamily: C.mono, fontSize: 11, color: C.muted, letterSpacing: '0.1em', marginBottom: 14, fontWeight: 600 }}>INJECTION SITE ROTATION</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setSide('front')} style={{ padding: '10px 16px', background: side === 'front' ? C.accent + '22' : 'transparent', color: side === 'front' ? C.accent : C.muted, border: `1px solid ${side === 'front' ? C.accent : C.border}`, borderRadius: 6, cursor: 'pointer', fontFamily: C.sans, fontWeight: 600, fontSize: 12 }}>Front</button>
          <button onClick={() => setSide('back')} style={{ padding: '10px 16px', background: side === 'back' ? C.accent + '22' : 'transparent', color: side === 'back' ? C.accent : C.muted, border: `1px solid ${side === 'back' ? C.accent : C.border}`, borderRadius: 6, cursor: 'pointer', fontFamily: C.sans, fontWeight: 600, fontSize: 12 }}>Back</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 20, alignItems: 'start' }}>
        <BodyDiagram side={side} sites={sites} hoveredSite={hoveredSite} setHoveredSite={setHoveredSite} onSiteClick={logInjection} />

        <div>
          {hoveredSite ? (
            (() => {
              const site = SITES.find(s => s.id === hoveredSite);
              const siteData = getSiteData(hoveredSite);
              const color = getColor(siteData.last_used);
              return (
                <div style={{ background: C.bg, border: `2px solid ${color}`, borderRadius: 8, padding: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 2 }}>{site.name}</div>
                  <div style={{ fontSize: 10, color, fontFamily: C.mono, marginBottom: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{color === C.green ? '✓ Ready' : color === C.yellow ? '⚠ Caution' : '✕ Avoid'}</div>
                  <div style={{ background: C.surface, padding: 11, borderRadius: 4, borderLeft: `3px solid ${color}`, marginBottom: 14 }}>
                    <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}><strong>Last:</strong> {getDaysAgo(siteData.last_used)}</div>
                    <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, marginTop: 6 }}><strong>Tip:</strong> {site.tip}</div>
                  </div>
                  <button onClick={() => logInjection(site.id)} style={{ width: '100%', padding: '10px', background: color, color: C.bg, border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 11, fontFamily: C.sans }}>Log Injection</button>
                </div>
              );
            })()
          ) : (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 16 }}>
              <div style={{ fontSize: 11, color: C.text, fontWeight: 600, marginBottom: 10 }}>Hover over a site</div>
              <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.9 }}>
                <div>👆 View details</div>
                <div>📍 Last injection</div>
                <div>✓ Ready to use?</div>
                <div>💉 Quick log</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 14 }}>
        <div style={{ fontSize: 11, color: C.muted, fontFamily: C.mono, marginBottom: 12, fontWeight: 600 }}>QUICK LOG</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(95px, 1fr))', gap: 8 }}>
          {displaySites.map(site => {
            const siteData = getSiteData(site.id);
            const color = getColor(siteData.last_used);
            return (
              <button key={site.id} onClick={() => logInjection(site.id)} style={{ padding: '9px', background: C.bg, border: `2px solid ${color}`, borderRadius: 6, cursor: 'pointer', fontSize: 9, color: C.text, fontFamily: C.sans, fontWeight: 600, lineHeight: 1.3 }}>
                <div>{site.name.split(' ').slice(1).join(' ')}</div>
                <div style={{ fontSize: 8, color, marginTop: 3, opacity: 0.9 }}>{getDaysAgo(siteData.last_used)}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ width: 10, height: 10, background: C.green, borderRadius: '50%', flexShrink: 0 }} />
            <div><div style={{ fontSize: 9, color: C.muted, fontFamily: C.mono, fontWeight: 700 }}>READY</div><div style={{ fontSize: 8, color: C.muted }}>14+ days</div></div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ width: 10, height: 10, background: C.yellow, borderRadius: '50%', flexShrink: 0 }} />
            <div><div style={{ fontSize: 9, color: C.muted, fontFamily: C.mono, fontWeight: 700 }}>CAUTION</div><div style={{ fontSize: 8, color: C.muted }}>7-14 days</div></div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ width: 10, height: 10, background: C.red, borderRadius: '50%', flexShrink: 0 }} />
            <div><div style={{ fontSize: 9, color: C.muted, fontFamily: C.mono, fontWeight: 700 }}>AVOID</div><div style={{ fontSize: 8, color: C.muted }}>&lt;7 days</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}